// ============================================================================
// SalaryGridEditor (F-663 / M-675/676) — editeur de la grille MASTER (GC-C)
// ============================================================================
// Port FIDELE du PoC app/(dashboard)/grille-salariale/salary-grid-editor.tsx
// (571 L @ SHA 7e40e4a). Cartes par classe + ClassEditorCard : Draft a 9 champs
// string, isDirty par snapshot JSON, delete arme 5 s, barre d'actions sticky,
// Δ echelon + annuel recalcules en direct. La MECANIQUE est conservee a
// l'identique (23 handlers PoC) ; seule la SURFACE change (P-CE3).
//
// TRANSPOSITION (PLAN-ATTAQUE §GC-C, strictement — pas de refonte d'etat) :
//  - server actions Next (useTransition) -> mutations TanStack (useGrilleCe) :
//    saveSalaryClass/deleteSalaryClass = TOAST (retour d'etat sans redirect,
//    P-CE16/P-CE18 : preserve scroll + brouillons des autres cartes) ;
//    loadDefaultGrid = ?ok=loaded (navigation cote page) ;
//  - re-skin @gs/ui : Collapsible PoC -> carte + entete cliquable locale ;
//    Input/Textarea/Table/Badge/Tooltip PoC -> tokens GS_COMPONENTS + primitives
//    locales (employee/ui Badge, GsTooltip) ; Intl fr-CA FIGE -> helpers locale
//    (format.ts, P-CE18) ; annees de colonnes = GRID_YEAR (source, pas hardcode).
//  - M-675 : le SERVEUR est l'AUTORITE des recalculs. Le client n'envoie que le
//    BRUT (rate, rate_2025, aug en %, labels) ; pct_from_prev, aug %/100 et la
//    reindexation 1..n sont recalcules serveur. Le payload MIROITE SavePayloadSchema.
//  - P-CE15 : payload.id = GUID de la classe MASTER (SalaryClass.id) ou '' (nouvelle) ;
//    le serveur resout le class_key (slugify si absent). Ne JAMAIS confondre les deux.
// Regle #4 : zero emoji. Regle #28 : tokens dark-aware, zero couleur en dur.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Download, Info, Loader2, Plus, Rows3, Save, Trash2, X } from 'lucide-react';
import { EmptyState, GsTooltip, useToast, GS_COMPONENTS } from '@gs/ui';

import { useTranslation } from '../../i18n';
import { cn, focusRing } from '../../lib/utils';
import { Badge } from '../employee/ui';
import { formatCad, formatHourly, formatPctPlain } from './format';
import { GRID_YEAR } from '../../lib/salary/progression';
import type { SalaryClass } from '../../lib/types/firestore';
import {
  useSaveSalaryClass,
  useDeleteSalaryClass,
  useLoadDefaultGrid,
  gridEditErrorCode,
  type SalaryClassSavePayload,
} from '../../hooks/useGrilleCe';

// ─── Draft client (VERBATIM PoC : 9 champs string, edition sans coercition) ────

type EchelonRow = { rate: string; rate_2025: string };
// echelon_max : vide = toute l'echelle. description : requis du niveau.
type NiveauRow = { label: string; echelon_max: string; description: string };

type Draft = {
  id: string | null; // null = nouvelle classe (GUID master sinon, P-CE15)
  label: string;
  description: string; // description generale de la classe
  sort_order: string;
  aug_grille_pct: string; // POURCENTAGE (ex. "2.5"), vide = aucune
  base_hours_per_week: string;
  weeks_per_year: string;
  echelons: EchelonRow[];
  niveaux: NiveauRow[];
};

function fromClass(c: SalaryClass): Draft {
  return {
    id: c.id,
    label: c.label,
    description: c.description ?? '',
    sort_order: String(c.sort_order),
    aug_grille_pct:
      c.aug_grille_pct === null ? '' : String(Math.round(c.aug_grille_pct * 10000) / 100),
    base_hours_per_week: String(c.base_hours_per_week),
    weeks_per_year: String(c.weeks_per_year),
    echelons: c.echelons.map((e) => ({
      rate: String(e.rate),
      rate_2025: e.rate_2025 === null ? '' : String(e.rate_2025),
    })),
    niveaux: c.niveaux.map((n) => ({
      label: n.label,
      echelon_max: n.echelon_max == null ? '' : String(n.echelon_max),
      description: n.description ?? '',
    })),
  };
}

function blankDraft(sortOrder: number): Draft {
  return {
    id: null,
    label: '',
    description: '',
    sort_order: String(sortOrder),
    aug_grille_pct: '',
    base_hours_per_week: '40',
    weeks_per_year: '52',
    echelons: [{ rate: '', rate_2025: '' }],
    niveaux: [{ label: '', echelon_max: '', description: '' }],
  };
}

function num(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

// Classes tokens partagees (dark-aware) — Input compact du tableau d'echelons.
const INPUT_SM = cn(GS_COMPONENTS.input, 'h-8 py-1');

export function SalaryGridEditor({ classes }: { classes: SalaryClass[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loadDefault = useLoadDefaultGrid();
  // Cartes « nouvelle classe » ajoutees cote client (avant sauvegarde). Le sort est
  // fige a l'ajout pour rester stable si une autre carte est retiree (VERBATIM PoC).
  const [newCards, setNewCards] = useState<{ key: number; sort: number }[]>([]);
  const nextSort = classes.reduce((m, c) => Math.max(m, c.sort_order), -1) + 1;

  function handleLoadDefault() {
    // P-CE2 : disabled pendant la mutation (double-clic bloque) ; ?ok=loaded a la fin.
    loadDefault.mutate(undefined, {
      onSuccess: () => navigate('/grille-salariale/edit?ok=loaded'),
      onError: (err) => navigate(`/grille-salariale/edit?error=${gridEditErrorCode(err)}`),
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleLoadDefault}
          disabled={loadDefault.isPending}
          className={cn(GS_COMPONENTS.btnSecondary, 'inline-flex touch-manipulation items-center gap-2 disabled:opacity-50')}
        >
          {loadDefault.isPending ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <Download size={16} aria-hidden="true" />
          )}
          {t('gest:grilleCe.editor.loadDefault')}
        </button>
        <button
          type="button"
          onClick={() =>
            setNewCards((cards) => [...cards, { key: Date.now(), sort: nextSort + cards.length }])
          }
          className={cn(GS_COMPONENTS.btnAction, 'inline-flex items-center gap-2')}
        >
          <Plus size={16} aria-hidden="true" />
          {t('gest:grilleCe.editor.addClass')}
        </button>
      </div>

      {classes.length === 0 && newCards.length === 0 ? (
        <EmptyState
          icon={<Rows3 size={48} aria-hidden="true" />}
          title={t('gest:grilleCe.editor.empty.title')}
          description={t('gest:grilleCe.editor.empty.description')}
        />
      ) : null}

      {classes.map((c) => (
        <ClassEditorCard key={c.id} initial={fromClass(c)} />
      ))}

      {newCards.map(({ key, sort }) => (
        <ClassEditorCard
          key={`new-${key}`}
          initial={blankDraft(sort)}
          onCancel={() => setNewCards((cards) => cards.filter((c) => c.key !== key))}
          onSaved={() => setNewCards((cards) => cards.filter((c) => c.key !== key))}
        />
      ))}
    </div>
  );
}

// Eyebrow de groupe a l'interieur d'une carte (hierarchie plate, pas de Section).
function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-chip font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
      {children}
    </p>
  );
}

function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="flex items-center gap-1 text-field font-medium text-slate-700 dark:text-slate-300">
      {children}
    </label>
  );
}

function ClassEditorCard({
  initial,
  onCancel,
  onSaved,
}: {
  initial: Draft;
  onCancel?: () => void;
  onSaved?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { toast } = useToast();
  const saveMut = useSaveSalaryClass();
  const deleteMut = useDeleteSalaryClass();
  const pending = saveMut.isPending || deleteMut.isPending;

  const [open, setOpen] = useState(initial.id === null);
  const [draft, setDraft] = useState<Draft>(initial);
  // Instantane de la derniere version sauvegardee : `initial` ne se reinitialise pas
  // apres un save (useState), on suit donc l'etat propre nous-memes (VERBATIM PoC).
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(initial));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const draftJson = JSON.stringify(draft);
  const isDirty = draftJson !== savedSnapshot;
  const hours = num(draft.base_hours_per_week) * num(draft.weeks_per_year);

  useEffect(() => {
    return () => {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
    };
  }, []);

  function patch(p: Partial<Draft>) {
    setDraft((d) => ({ ...d, ...p }));
  }
  function setEchelon(i: number, p: Partial<EchelonRow>) {
    setDraft((d) => ({ ...d, echelons: d.echelons.map((e, j) => (j === i ? { ...e, ...p } : e)) }));
  }
  function addEchelon() {
    setDraft((d) => ({ ...d, echelons: [...d.echelons, { rate: '', rate_2025: '' }] }));
  }
  function removeEchelon(i: number) {
    setDraft((d) => ({ ...d, echelons: d.echelons.filter((_, j) => j !== i) }));
  }
  function setNiveau(i: number, p: Partial<NiveauRow>) {
    setDraft((d) => ({ ...d, niveaux: d.niveaux.map((n, j) => (j === i ? { ...n, ...p } : n)) }));
  }
  function addNiveau() {
    setDraft((d) => ({ ...d, niveaux: [...d.niveaux, { label: '', echelon_max: '', description: '' }] }));
  }
  function removeNiveau(i: number) {
    setDraft((d) => ({ ...d, niveaux: d.niveaux.filter((_, j) => j !== i) }));
  }

  // Payload MIROIR SavePayloadSchema serveur (M-675 : le serveur recalcule pct/aug/index).
  const payload: SalaryClassSavePayload = {
    id: draft.id ?? '',
    label: draft.label,
    description: draft.description.trim() === '' ? null : draft.description.trim(),
    sort_order: num(draft.sort_order),
    aug_grille_pct: draft.aug_grille_pct.trim() === '' ? null : num(draft.aug_grille_pct),
    base_hours_per_week: num(draft.base_hours_per_week),
    weeks_per_year: num(draft.weeks_per_year),
    echelons: draft.echelons.map((e) => ({
      rate: num(e.rate),
      rate_2025: e.rate_2025.trim() === '' ? null : num(e.rate_2025),
    })),
    niveaux: draft.niveaux
      .filter((n) => n.label.trim() !== '')
      .map((n) => ({
        label: n.label.trim(),
        echelon_max: n.echelon_max.trim() === '' ? null : num(n.echelon_max),
        description: n.description.trim() === '' ? null : n.description.trim(),
      })),
  };

  function handleSave() {
    saveMut.mutate(payload, {
      onSuccess: () => {
        // Snapshot local mis a jour -> isDirty repasse false SANS refetch destructif.
        setSavedSnapshot(draftJson);
        toast(t('gest:grilleCe.editor.toast.saved'), 'success');
        onSaved?.();
      },
      onError: (err) => {
        toast(t(`gest:grilleCe.editErrors.${gridEditErrorCode(err)}`, {
          defaultValue: t('gest:grilleCe.editErrors.server'),
        }), 'error');
      },
    });
  }

  function handleDelete() {
    if (!draft.id) return;
    const id = draft.id;
    deleteMut.mutate(id, {
      onSuccess: () => toast(t('gest:grilleCe.editor.toast.deleted'), 'success'),
      onError: (err) => {
        setConfirmDelete(false);
        toast(t(`gest:grilleCe.editErrors.${gridEditErrorCode(err)}`, {
          defaultValue: t('gest:grilleCe.editErrors.server'),
        }), 'error');
      },
    });
  }

  function armDeleteConfirm() {
    setConfirmDelete(true);
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    confirmTimer.current = setTimeout(() => setConfirmDelete(false), 5000);
  }

  // Resume compact affiche carte repliee (suit le brouillon en direct).
  const rates = draft.echelons.map((e) => num(e.rate)).filter((r) => r > 0);
  const rateRange =
    rates.length > 0
      ? `${formatHourly(Math.min(...rates), locale)} – ${formatHourly(Math.max(...rates), locale)}`
      : '—';

  const summary = (
    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
      <span className="truncate font-medium text-slate-800 dark:text-slate-100">
        {draft.label || t('gest:grilleCe.editor.newClass')}
      </span>
      {isDirty ? <Badge tone="warning">{t('gest:grilleCe.editor.dirtyBadge')}</Badge> : null}
      <span className="flex items-center gap-3 text-chip font-normal text-slate-500 dark:text-slate-400">
        <span>{t('gest:grilleCe.editor.echelonCount', { count: draft.echelons.length })}</span>
        <span className="tabular-nums">{rateRange}</span>
        {draft.aug_grille_pct.trim() !== '' ? (
          <span>{t('gest:grilleCe.editor.augShort', { pct: draft.aug_grille_pct })}</span>
        ) : null}
      </span>
    </span>
  );

  const rateYear = GRID_YEAR; // annee de reference de la master (pas de hardcode)
  const prevYear = GRID_YEAR - 1;

  return (
    <div className={cn(GS_COMPONENTS.card, 'p-0')}>
      {/* Entete cliquable (Collapsible PoC re-skinne : carte + summary + chevron). */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={
          open
            ? t('gest:grilleCe.editor.collapseAria', { name: draft.label || t('gest:grilleCe.editor.newClass') })
            : t('gest:grilleCe.editor.expandAria', { name: draft.label || t('gest:grilleCe.editor.newClass') })
        }
        className={cn('flex w-full items-center gap-2 rounded-t-xl px-4 py-3 text-left', focusRing)}
      >
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={cn('shrink-0 text-slate-400 transition-transform', open && 'rotate-180')}
        />
        {summary}
      </button>

      {open ? (
        <div className="flex flex-col gap-6 px-4 pb-0 pt-1">
          {/* Informations */}
          <div className="flex flex-col gap-3">
            <GroupLabel>{t('gest:grilleCe.editor.group.info')}</GroupLabel>
            <div className="grid gap-3 sm:grid-cols-6">
              <div className="flex flex-col gap-1.5 sm:col-span-3">
                <FieldLabel>{t('gest:grilleCe.editor.field.label')}</FieldLabel>
                <input
                  value={draft.label}
                  onChange={(e) => patch({ label: e.target.value })}
                  placeholder={t('gest:grilleCe.editor.ph.label')}
                  className={GS_COMPONENTS.input}
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <FieldLabel>
                  {t('gest:grilleCe.editor.field.aug')}
                  <GsTooltip content={t('gest:grilleCe.editor.tooltip.aug')}>
                    <Info className="size-3.5 text-slate-400" aria-hidden="true" />
                  </GsTooltip>
                </FieldLabel>
                <input
                  value={draft.aug_grille_pct}
                  onChange={(e) => patch({ aug_grille_pct: e.target.value })}
                  inputMode="decimal"
                  placeholder={t('gest:grilleCe.editor.ph.aug')}
                  className={GS_COMPONENTS.input}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel>{t('gest:grilleCe.editor.field.sortOrder')}</FieldLabel>
                <input
                  value={draft.sort_order}
                  onChange={(e) => patch({ sort_order: e.target.value })}
                  inputMode="numeric"
                  className={GS_COMPONENTS.input}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <FieldLabel>{t('gest:grilleCe.editor.field.description')}</FieldLabel>
              <textarea
                value={draft.description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder={t('gest:grilleCe.editor.ph.description')}
                rows={3}
                className={cn(GS_COMPONENTS.input, 'resize-y')}
              />
            </div>
          </div>

          {/* Horaire */}
          <div className="flex flex-col gap-3">
            <GroupLabel>{t('gest:grilleCe.editor.group.hours')}</GroupLabel>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <FieldLabel>{t('gest:grilleCe.editor.field.hoursPerWeek')}</FieldLabel>
                <input
                  value={draft.base_hours_per_week}
                  onChange={(e) => patch({ base_hours_per_week: e.target.value })}
                  inputMode="decimal"
                  className={GS_COMPONENTS.input}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel>{t('gest:grilleCe.editor.field.weeksPerYear')}</FieldLabel>
                <input
                  value={draft.weeks_per_year}
                  onChange={(e) => patch({ weeks_per_year: e.target.value })}
                  inputMode="decimal"
                  className={GS_COMPONENTS.input}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel>{t('gest:grilleCe.editor.field.hoursPerYear')}</FieldLabel>
                <p className="flex h-9 items-center text-sm tabular-nums text-slate-500 dark:text-slate-400">
                  {hours > 0 ? hours : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Niveaux (roles + plage d'avancement) */}
          <div className="flex flex-col gap-2">
            <GroupLabel>{t('gest:grilleCe.editor.group.niveaux')}</GroupLabel>
            {draft.niveaux.map((n, i) => (
              <div
                key={i}
                className="flex flex-col gap-2 rounded-lg border border-dashed border-slate-300 p-3 dark:border-slate-600"
              >
                <div className="flex items-center gap-2">
                  <span className="w-16 shrink-0 text-sm text-slate-500 dark:text-slate-400">
                    {t('gest:grilleCe.editor.niveauN', { n: i + 1 })}
                  </span>
                  <input
                    value={n.label}
                    onChange={(e) => setNiveau(i, { label: e.target.value })}
                    placeholder={t('gest:grilleCe.editor.ph.niveauLabel')}
                    className={cn(GS_COMPONENTS.input, 'flex-1')}
                  />
                  <span className="text-chip text-slate-500 dark:text-slate-400">
                    {t('gest:grilleCe.editor.echMax')}
                  </span>
                  <input
                    value={n.echelon_max}
                    onChange={(e) => setNiveau(i, { echelon_max: e.target.value })}
                    inputMode="numeric"
                    placeholder={String(draft.echelons.length)}
                    className={cn(GS_COMPONENTS.input, 'w-16 text-right')}
                  />
                  <button
                    type="button"
                    aria-label={t('gest:grilleCe.editor.removeNiveauAria', { n: i + 1 })}
                    onClick={() => removeNiveau(i)}
                    className={cn(GS_COMPONENTS.btnIcon, 'size-8 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700')}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                </div>
                <textarea
                  value={n.description}
                  onChange={(e) => setNiveau(i, { description: e.target.value })}
                  placeholder={t('gest:grilleCe.editor.ph.niveauDesc', { n: i + 1 })}
                  rows={3}
                  className={cn(GS_COMPONENTS.input, 'resize-y')}
                />
              </div>
            ))}
            <div>
              <button
                type="button"
                onClick={addNiveau}
                className={cn(GS_COMPONENTS.btnSecondary, 'inline-flex items-center gap-2')}
              >
                <Plus size={16} aria-hidden="true" />
                {t('gest:grilleCe.editor.addNiveau')}
              </button>
            </div>
          </div>

          {/* Echelons */}
          <div className="flex flex-col gap-2">
            <GroupLabel>{t('gest:grilleCe.editor.group.echelons')}</GroupLabel>
            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full min-w-max border-collapse">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800">
                    <th className="px-2 py-2 text-left text-chip font-semibold uppercase tracking-wide text-slate-500">#</th>
                    <th className="px-2 py-2 text-right text-chip font-semibold uppercase tracking-wide text-slate-500">
                      {t('gest:grilleCe.editor.col.rate', { year: rateYear })}
                    </th>
                    <th className="px-2 py-2 text-right text-chip font-semibold uppercase tracking-wide text-slate-500">
                      {t('gest:grilleCe.editor.col.prevRate', { year: prevYear })}
                    </th>
                    <th className="px-2 py-2 text-right text-chip font-semibold uppercase tracking-wide text-slate-500">
                      {t('gest:grilleCe.editor.col.delta')}
                    </th>
                    <th className="px-2 py-2 text-right text-chip font-semibold uppercase tracking-wide text-slate-500">
                      {t('gest:grilleCe.editor.col.annual')}
                    </th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {draft.echelons.map((e, i) => {
                    const rate = num(e.rate);
                    const prev = i > 0 ? num(draft.echelons[i - 1].rate) : 0;
                    const delta = i > 0 && prev > 0 ? rate / prev - 1 : null;
                    return (
                      <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="px-2 py-1 text-sm tabular-nums text-slate-500 dark:text-slate-400">
                          {i + 1}
                        </td>
                        <td className="px-2 py-1">
                          <input
                            value={e.rate}
                            onChange={(ev) => setEchelon(i, { rate: ev.target.value })}
                            inputMode="decimal"
                            className={cn(INPUT_SM, 'text-right')}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            value={e.rate_2025}
                            onChange={(ev) => setEchelon(i, { rate_2025: ev.target.value })}
                            inputMode="decimal"
                            placeholder="—"
                            className={cn(INPUT_SM, 'text-right')}
                          />
                        </td>
                        <td className="px-2 py-1 text-right text-sm tabular-nums text-slate-500 dark:text-slate-400">
                          {delta !== null ? formatPctPlain(delta, locale) : '—'}
                        </td>
                        <td className="px-2 py-1 text-right text-sm tabular-nums text-slate-500 dark:text-slate-400">
                          {rate > 0 ? formatCad(Math.round(rate * hours), locale) : '—'}
                        </td>
                        <td className="px-2 py-1 text-right">
                          <button
                            type="button"
                            aria-label={t('gest:grilleCe.editor.removeEchelonAria', { n: i + 1 })}
                            onClick={() => removeEchelon(i)}
                            className={cn(GS_COMPONENTS.btnIcon, 'size-8 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700')}
                          >
                            <X size={16} aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div>
              <button
                type="button"
                onClick={addEchelon}
                className={cn(GS_COMPONENTS.btnSecondary, 'inline-flex items-center gap-2')}
              >
                <Plus size={16} aria-hidden="true" />
                {t('gest:grilleCe.editor.addEchelon')}
              </button>
            </div>
          </div>

          {/* Actions — barre sticky visible pendant le defilement de la carte. */}
          <div className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center gap-2 rounded-b-xl border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:border-slate-700 dark:bg-slate-800/95 dark:supports-[backdrop-filter]:bg-slate-800/80">
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || pending || draft.label.trim() === ''}
              className={cn(GS_COMPONENTS.btnAction, 'inline-flex items-center gap-2 disabled:opacity-50')}
            >
              {saveMut.isPending ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <Save size={16} aria-hidden="true" />
              )}
              {t('gest:grilleCe.editor.save')}
            </button>
            {isDirty ? (
              <span className="text-chip text-slate-500 dark:text-slate-400">
                {t('gest:grilleCe.editor.unsaved')}
              </span>
            ) : null}
            <span className="ml-auto flex items-center gap-2">
              {draft.id ? (
                confirmDelete ? (
                  <>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={pending}
                      className={cn(GS_COMPONENTS.btnDanger, 'inline-flex items-center gap-2 disabled:opacity-50')}
                    >
                      {deleteMut.isPending ? (
                        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                      ) : (
                        <Trash2 size={16} aria-hidden="true" />
                      )}
                      {t('gest:grilleCe.editor.confirmDelete')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      className={cn(GS_COMPONENTS.btnGhost, 'px-3 py-2')}
                    >
                      {t('gest:grilleCe.editor.cancel')}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={armDeleteConfirm}
                    className={cn(GS_COMPONENTS.btnGhost, 'inline-flex items-center gap-2 px-3 py-2')}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    {t('gest:grilleCe.editor.delete')}
                  </button>
                )
              ) : onCancel ? (
                <button
                  type="button"
                  onClick={onCancel}
                  className={cn(GS_COMPONENTS.btnGhost, 'px-3 py-2')}
                >
                  {t('gest:grilleCe.editor.cancel')}
                </button>
              ) : null}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
